/* ==========================================================================
   email-popup.js :: snippets/email-popup.liquid behaviour

   2026-08-22 (founder) — TRIGGERS REPLACED. Was: show once per 30 days
   (localStorage `ordin_popup_seen`), fired by desktop exit-intent or, on a
   coarse pointer, a 20 s timer / 50% scroll. Now:

     WHERE  ALLOWLIST — the homepage and product pages only. Everywhere else
            it never runs. This subsumes the old /cart, /checkouts,
            /pages/library* blocklist.
     WHEN   Immediately on load. No exit-intent, no timer, no scroll trigger.
            A ~300 ms defer lets first paint and any hash focus settle.
     HOW    Once per SESSION (sessionStorage `ordin_popup_session`), not per
            30 days. Any stale `ordin_popup_seen` localStorage key left on a
            returning visitor's browser is deleted on load.

   Unchanged: never while the cart drawer ([data-cart-drawer].is-open), the
   mobile nav (.mobile-nav.is-open) or Shopify's consent banner
   (#shopify-pc__banner) is showing — a blocked request is never consumed, it
   re-checks every 2 s (5 min cap) and on visitorConsentCollected. Never in
   the theme editor. Escape + backdrop close; focus moves to the close button
   on open and returns on close; body scroll locked while open
   (html.popup-lock); Ordin.lenis stop/start null-safe.

   So with the consent banner up the intended sequence is: banner first,
   popup ~2 s after the visitor dismisses it.

   Success path: Shopify reloads with ?customer_posted=true#popup-newsletter-form
   and the snippet renders the thank-you state server-side; we open it once
   and do NOT re-arm.

   2026-08-26 (presentation rebuild, source lineage 9c58216): the snippet
   became a FULL-PAGE TAKEOVER (full-viewport photo ground, centered 340px
   column, "No thank you" text button beside the top-right X). NOTHING IN THE
   CONTRACT ABOVE MOVED — the allowlist, the once-per-session sessionStorage
   suppression, the blocked() wait/poll, the post-submit states and the form
   itself are byte-for-byte the same. Both close routes are [data-popup-close],
   so both run close(), and because markSeen() is armed on OPEN (not on close)
   either route suppresses the takeover for the rest of the session. The only
   addition is a local scroll lock: html.popup-lock alone is a class hook that
   assets/components.css turns into html{overflow:hidden}, which native mobile
   Safari does not always honour on its own, so lockScroll()/unlockScroll()
   below ALSO set inline overflow:hidden on <html> AND <body> and pad the body
   by the scrollbar width -- the same idiom assets/cart.js uses, reimplemented
   here (cart.js is never imported and is not touched).

   2026-08-22 (launch-blocking): this file must NEVER put the popup into
   display:none. Shopify's storefront-forms-hCaptcha script binds every
   {% form 'customer' %} at page load — it appends a div.h-captcha to the form,
   calls hcaptcha.render() on it, and swaps form.submit for
   `hcaptcha.execute().then(nativeSubmit)`. An iframe inside a display:none
   subtree never completes, so execute() never resolves and Subscribe hangs on
   "Sending" with no POST. Open/close is therefore purely the .is-open class;
   the closed state is visibility:hidden + opacity:0 + pointer-events:none in
   assets/components.css.
   ========================================================================== */
(function () {
  'use strict';

  var popup = document.querySelector('[data-email-popup]');
  if (!popup) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var SESSION_KEY = 'ordin_popup_session';
  var LEGACY_KEY = 'ordin_popup_seen';
  var FORM_ID = 'popup-newsletter-form';
  /* (the prefers-reduced-motion check that used to live here only existed to
     skip the 260ms timer before setting the `hidden` attribute; with `hidden`
     gone the CSS handles reduced motion on its own — components.css already
     zeroes these transitions in its prefers-reduced-motion block) */

  /* Retire the old 30-day key so nothing lingers on a returning visitor. */
  try { window.localStorage.removeItem(LEGACY_KEY); } catch (e) { /* private mode */ }

  /* Allowlist: homepage + product pages only.
     routes.root_url arrives as data-root so this still matches on a locale-
     or market-prefixed storefront. Shopify emits "/" for the primary locale
     and "/fr" (no trailing slash) for a prefixed one, so normalise both:
     strip any trailing slash to get the prefix, then peel it off the path.
       "/fr"            -> "/"
       "/fr/"           -> "/"
       "/fr/products/x" -> "/products/x"  */
  var prefix = (popup.getAttribute('data-root') || '/').replace(/\/+$/, '');
  var path = window.location.pathname;
  if (prefix) {
    if (path === prefix) path = '/';
    else if (path.indexOf(prefix + '/') === 0) path = path.slice(prefix.length);
  }
  if (path.length > 1) path = path.replace(/\/+$/, '') || '/';
  var allowedPath = path === '/' || path.indexOf('/products/') === 0;

  var card = popup.querySelector('.email-popup__card');
  var closeBtn = popup.querySelector('.email-popup__close');
  var lastFocus = null;
  var shown = false;

  /* 2026-10-26 (QA, iOS Simulator): sessionStorage is per TAB, so on iPhone every link opened in a new tab (ads, Safari
     "open in new tab") counted as a new visit and the popup came back on every page. A visit is now also remembered in
     localStorage and lasts while the shopper keeps browsing (30 min since the last page view). */
  var VISIT_KEY = 'ordin_popup_visit', VISIT_MS = 30 * 60 * 1000;
  function markSeen() {
    try { window.sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* private mode */ }
    try { window.localStorage.setItem(VISIT_KEY, String(Date.now())); } catch (e) {}
  }
  function seenThisSession() {
    var seen = false;
    try { seen = window.sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) {}
    try {
      var t = +window.localStorage.getItem(VISIT_KEY) || 0;
      if (t && Date.now() - t < VISIT_MS) { seen = true; window.localStorage.setItem(VISIT_KEY, String(Date.now())); }
    } catch (e) {}
    return seen;
  }
  /* Anything that must not be covered by (or sit over) the popup:
     cart drawer (z 150), mobile-nav overlay (z 140, global.js toggles
     .is-open), and Shopify's privacy/cookie banner (#shopify-pc__banner,
     z 2,000,000 — cannot be outranked, so we wait for it to go). A blocked
     trigger is never consumed; the timer path re-checks every 2 s. */
  function blocked() {
    var d = document.querySelector('[data-cart-drawer]');
    if (d && d.classList.contains('is-open')) return true;
    if (document.querySelector('.mobile-nav.is-open')) return true;
    if (document.querySelector('.pdp-modal.is-open')) return true; /* 2026-10-22: never over the guarantee / gift modals */
    /* the banner is position:fixed, so offsetParent is null even when it
       is showing -- test box + computed display/visibility instead */
    var pc = document.getElementById('shopify-pc__banner');
    if (pc && pc.getBoundingClientRect().height > 0) {
      var cs = window.getComputedStyle(pc);
      if (cs.display !== 'none' && cs.visibility !== 'hidden') return true;
    }
    return false;
  }
  function setLenis(stop) {
    var lenis = window.Ordin && window.Ordin.lenis;
    if (!lenis) return;
    if (stop) { lenis.stop(); } else { lenis.start(); }
  }

  /* ------------------------------------------------------------------
     Page scroll lock (2026-08-26, full-page takeover). Keeps the
     html.popup-lock class that assets/components.css already keys off,
     and additionally locks BOTH <html> and <body> inline -- Lenis is
     null on touch, so the class alone will not stop native mobile
     scroll behind a full-viewport overlay. The body is padded by the
     scrollbar width so the page underneath does not reflow on lock.
     Guarded by scrollLocked so repeated open/close cannot double-apply
     or leave the page stuck. Same idiom as assets/cart.js lockScroll(),
     implemented locally -- cart.js is not imported.
     ------------------------------------------------------------------ */
  var scrollLocked = false;
  function lockScroll() {
    if (scrollLocked) return;
    scrollLocked = true;
    /* 2026-10-27: shared events lock (assets/scroll-lock.js); no overflow toggle */
    if (window.OrdinScrollLock) window.OrdinScrollLock.lock('email-popup', popup);
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    if (window.OrdinScrollLock) window.OrdinScrollLock.unlock('email-popup');
  }

  function open() {
    if (shown) return;
    shown = true;
    lastFocus = document.activeElement;
    /* 2026-08-22: no `popup.hidden = false` any more — the element is never
       display:none, or Shopify's hCaptcha binding breaks (see the snippet).
       The style flush still matters so the opacity transition actually runs
       from 0 rather than being coalesced (no rAF: it stalls in hidden tabs). */
    void popup.offsetWidth;
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    /* focus once visible (a visibility:hidden button refuses focus) */
    if (closeBtn) closeBtn.focus();
    lockScroll();
    setLenis(true);
    markSeen();
  }

  function close() {
    if (!popup.classList.contains('is-open')) return;
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    unlockScroll();
    setLenis(false);
    /* Removing .is-open is the whole close: the base rule takes opacity back
       to 0, flips visibility to hidden after the transition, and restores
       pointer-events:none. Nothing sets the `hidden` attribute any more. */
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
  }

  popup.querySelectorAll('[data-popup-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });

  /* 2026-10-25 (founder, PostHog: 60+ dead swipes on the pop-up): a downward swipe closes it on
     phones, exactly like the close button (same close(); the once-per-session mark was set on open).
     Only when the content is scrolled to its top, so a swipe can still scroll a tall form; inputs
     are left alone. The card follows the finger and springs back on a short swipe. */
  (function () {
    var y0 = null, x0 = 0, dy = 0, dx = 0, onBg = false;
    function scrollerTop(node) {
      for (var n = node; n && n !== popup.parentNode; n = n.parentNode) {
        if (n.scrollHeight > n.clientHeight + 2 && n.scrollTop > 0) return false;
      }
      return true;
    }
    popup.addEventListener('touchstart', function (e) {
      if (!popup.classList.contains('is-open') || e.touches.length !== 1) { y0 = null; return; }
      var t = e.target;
      if (t && t.closest && t.closest('input, textarea, select')) { y0 = null; return; }
      onBg = !!(t && t.closest && t.closest('.email-popup__backdrop'));
      y0 = onBg || scrollerTop(t) ? e.touches[0].clientY : null; x0 = e.touches[0].clientX; dy = 0; dx = 0;
    }, { passive: true });
    popup.addEventListener('touchmove', function (e) {
      if (y0 === null || !card) return;
      dy = e.touches[0].clientY - y0; dx = e.touches[0].clientX - x0;
      if (dy > 0 && dy > Math.abs(e.touches[0].clientX - x0)) { card.style.transition = 'none'; card.style.transform = 'translateY(' + dy + 'px)'; card.style.opacity = String(Math.max(.4, 1 - dy / 500)); }
    }, { passive: true });
    popup.addEventListener('touchend', function () {
      if (y0 === null) return;
      y0 = null;
      if (card) { card.style.transition = ''; card.style.transform = ''; card.style.opacity = ''; }
      if (dy > 80 || (onBg && Math.hypot(dx, dy) > 40)) close(); /* any swipe on the backdrop also closes */
    });
  })();

  document.addEventListener('keydown', function (e) {
    if (!popup.classList.contains('is-open')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var focusables = card.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---- 1. post-submit states: thank-you (once) or validation error ---- */
  var posted = /[?&]customer_posted=true/.test(window.location.search) && window.location.hash === '#' + FORM_ID;
  var hasSuccess = !!popup.querySelector('[data-popup-success]');
  var hasError = !!popup.querySelector('[data-popup-error]');
  if ((posted && hasSuccess) || (hasError && window.location.hash === '#' + FORM_ID)) {
    open();
    /* the hash navigation can steal focus after our early focus() call */
    window.addEventListener('load', function () {
      if (closeBtn && popup.classList.contains('is-open')) closeBtn.focus();
    });
    return; /* do not re-arm triggers on this load */
  }

  /* ---- 2. trigger: instant, homepage + PDP only, once per session ---- */
  if (!allowedPath || seenThisSession()) return;

  function tryOpen() {
    if (shown) return false;
    if (blocked()) return false;   /* overlay/banner up: keep the trigger armed */
    open();
    return true;
  }

  /* "wanted" = a trigger has fired but was blocked; poll every 2 s until
     clear (capped at 5 min), and also re-check the moment Shopify's
     consent banner reports a choice. */
  var wanted = false;
  var poll = null;
  var pollStart = 0;
  var POLL_MS = 2000;
  var POLL_CAP = 5 * 60 * 1000;
  function stopPoll() {
    if (poll) { window.clearInterval(poll); poll = null; }
  }
  function request() {
    if (shown) return true;
    if (tryOpen()) { stopPoll(); return true; }
    wanted = true;
    if (!poll) {
      pollStart = Date.now();
      poll = window.setInterval(function () {
        if (shown || tryOpen() || (Date.now() - pollStart) > POLL_CAP) stopPoll();
      }, POLL_MS);
    }
    return false;
  }
  document.addEventListener('visitorConsentCollected', function () {
    if (wanted && !shown) window.setTimeout(request, 50);
  });

  /* Fire immediately. The 300 ms defer is not a delay in the old sense — it
     just lets first paint and any hash-driven focus settle before we move
     focus to the close button, so the open does not fight the browser.
     If anything is blocking (consent banner, drawer, mobile nav) request()
     keeps it pending and the 2 s poll / visitorConsentCollected picks it up,
     which is what produces "banner first, popup right after dismissal". */
  /* 2026-10-25 round 10 (founder): show it ~1.5 s after load, ONCE per visit (the session mark set on open), and never again
     once they have signed up (localStorage ordin_popup_signed, set on submit and on the success screen). The 30 s /
     past-the-buy-box trigger is retired. Swipe-to-dismiss (above) is unchanged. */
  var signed = false;
  try { signed = window.localStorage.getItem('ordin_popup_signed') === '1'; } catch (e) { signed = false; }
  if (signed) return;
  /* round 14 (founder): the popup's 48 h clock starts the first time a visitor sees it (snippets/email-popup.liquid,
     localStorage ordin_code_t0); once it runs out the popup never shows again for that visitor. */
  try { var cd0 = +window.localStorage.getItem('ordin_code_t0') || 0; if (cd0 && Date.now() - cd0 > 48 * 3600 * 1000) return; } catch (e) {}
  window.setTimeout(request, 1500);
})();

/* 2026-10-16 (PostHog dead clicks on the pop-up's gift image): a tap on the win-screen image
   takes the visitor to the email box instead of doing nothing. */
document.addEventListener('click', function (e) {
  var img = e.target instanceof Element ? e.target.closest('[data-ep-focus-email]') : null;
  if (!img) return;
  var input = document.getElementById('popup-newsletter-email');
  if (!input) return;
  try { input.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) {}
  input.focus({ preventScroll: true });
});
